/**
 * Utility functions for handling cell values in editable tables
 * Provides type-safe value normalization, comparison, and type guards
 */

// Union type for all possible cell values
export type CellValue = string | number | Date | boolean | null | undefined | Array<{id: string; [key: string]: unknown}> | string[];

/**
 * Type guard to check if an item is an object with an id property
 */
export function isObjectWithId(item: unknown): item is { id: string } {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    typeof (item as { id?: unknown }).id === 'string'
  );
}

/**
 * Type guard to check if an object has a specific string property
 */
export function hasStringProperty(obj: unknown, prop: string): boolean {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    prop in obj &&
    typeof (obj as Record<string, unknown>)[prop] === 'string'
  );
}

/**
 * Safely extract IDs from an array of items
 */
export function extractIds(items: unknown[]): string[] {
  return items
    .map((item) => {
      if (isObjectWithId(item)) {
        return item.id;
      }
      if (typeof item === 'string') {
        return item;
      }
      return '';
    })
    .filter(Boolean);
}

/**
 * Normalize values for comparison based on input type
 */
export function normalizeValueForComparison(
  currentValue: string,
  originalValue: CellValue,
  inputType: string = 'text'
): {
  normalizedNewValue: string | number | null;
  normalizedOriginalValue: string | number | null;
} {
  let normalizedNewValue: string | number | null = currentValue;
  let normalizedOriginalValue: string | number | null;

  // Safely determine the normalized original value based on its type
  if (originalValue === null || originalValue === undefined) {
    normalizedOriginalValue = null;
  } else if (typeof originalValue === 'string') {
    normalizedOriginalValue = originalValue;
  } else if (typeof originalValue === 'number') {
    normalizedOriginalValue = originalValue;
  } else if (typeof originalValue === 'boolean') {
    normalizedOriginalValue = originalValue.toString();
  } else if (originalValue instanceof Date) {
    normalizedOriginalValue = originalValue.toISOString();
  } else if (Array.isArray(originalValue)) {
    // Handle arrays by converting to string representation
    normalizedOriginalValue = originalValue.length > 0 ? JSON.stringify(originalValue) : '';
  } else {
    // Fallback for any other types
    normalizedOriginalValue = String(originalValue);
  }

  if (inputType === 'number') {
    // For numbers, compare numeric values
    normalizedNewValue = currentValue === '' ? null : Number(currentValue);
    normalizedOriginalValue = 
      originalValue === '' || originalValue === null || originalValue === undefined 
        ? null 
        : Number(originalValue);
  } else {
    // For text, compare string values
    normalizedNewValue = currentValue;
    normalizedOriginalValue = normalizedOriginalValue === null ? '' : String(normalizedOriginalValue);
  }

  return {
    normalizedNewValue,
    normalizedOriginalValue,
  };
}

/**
 * Check if a value has actually changed after normalization
 */
export function hasValueChanged(
  currentValue: string,
  originalValue: CellValue,
  inputType: string = 'text'
): boolean {
  const { normalizedNewValue, normalizedOriginalValue } = normalizeValueForComparison(
    currentValue,
    originalValue,
    inputType
  );

  return normalizedNewValue !== normalizedOriginalValue;
}

/**
 * Handle save or cancel logic based on whether a value has changed
 */
export function handleValueChangeAction(
  currentValue: string,
  originalValue: CellValue,
  inputType: string = 'text',
  onSave: (value: string) => void,
  onCancel: () => void
): void {
  if (hasValueChanged(currentValue, originalValue, inputType)) {
    onSave(currentValue);
  } else {
    // No changes, cancel the edit mode
    onCancel();
  }
}